import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../lib/api';
import { fmtHours } from '../lib/formatters';
import { useAuth } from '../hooks/useAuth';
import { Plus, Download, FileText, X } from 'lucide-react';

// ---- schema ----
const invoiceSchema = z.object({
  userId: z.string().min(1, 'Select an employee'),
  projectId: z.string().min(1, 'Select a project'),
  dateFrom: z.string().min(1, 'Start date is required'),
  dateTo: z.string().min(1, 'End date is required'),
}).refine((d) => !d.dateFrom || !d.dateTo || d.dateTo >= d.dateFrom, {
  message: 'End date must be on or after start date',
  path: ['dateTo'],
});

// ---- helpers ----
const STATUS_STYLES = {
  DRAFT: 'bg-gray-100 text-gray-600',
  SENT: 'bg-blue-100 text-blue-700',
  PAID: 'bg-green-100 text-green-700',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.DRAFT}`}>
      {status}
    </span>
  );
}

function fmtCurrency(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
}

function fmtDate(iso) {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---- generate invoice modal ----
function GenerateInvoiceModal({ onClose, onSuccess, users, projects }) {
  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(invoiceSchema) });

  const watched = useWatch({ control });

  // Preview calculation
  const { data: preview } = useQuery({
    queryKey: ['invoice-preview', watched.userId, watched.projectId, watched.dateFrom, watched.dateTo],
    queryFn: async () => {
      if (!watched.userId || !watched.projectId || !watched.dateFrom || !watched.dateTo) return null;
      const p = new URLSearchParams({
        userId: watched.userId,
        projectId: watched.projectId,
        dateFrom: watched.dateFrom,
        dateTo: watched.dateTo,
      });
      const res = await api.get(`/api/invoices/preview?${p.toString()}`);
      return res.data;
    },
    enabled: !!(watched.userId && watched.projectId && watched.dateFrom && watched.dateTo),
  });

  const selectedUser = users.find((u) => u.id === watched.userId);

  const onSubmit = async (values) => {
    try {
      await api.post('/api/invoices/generate', values);
      onSuccess();
    } catch (err) {
      setError('root', { message: err?.response?.data?.error || 'Failed to generate invoice.' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Generate Invoice</h2>
          <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4" noValidate>
          {errors.root && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {errors.root.message}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
              <select
                {...register('userId')}
                className={`w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.userId ? 'border-red-400' : 'border-gray-300'}`}
              >
                <option value="">Select employee...</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
              {errors.userId && <p className="mt-1 text-xs text-red-600">{errors.userId.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select
                {...register('projectId')}
                className={`w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.projectId ? 'border-red-400' : 'border-gray-300'}`}
              >
                <option value="">Select project...</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {errors.projectId && <p className="mt-1 text-xs text-red-600">{errors.projectId.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                {...register('dateFrom')}
                className={`w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.dateFrom ? 'border-red-400' : 'border-gray-300'}`}
              />
              {errors.dateFrom && <p className="mt-1 text-xs text-red-600">{errors.dateFrom.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                {...register('dateTo')}
                className={`w-full px-3 py-2 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.dateTo ? 'border-red-400' : 'border-gray-300'}`}
              />
              {errors.dateTo && <p className="mt-1 text-xs text-red-600">{errors.dateTo.message}</p>}
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2 text-sm">
              <p className="font-semibold text-blue-900">Invoice Preview</p>
              <div className="flex justify-between text-blue-800">
                <span>Hours logged</span>
                <span className="font-medium">{fmtHours(preview.hours)}</span>
              </div>
              <div className="flex justify-between text-blue-800">
                <span>Hourly rate</span>
                <span className="font-medium">{fmtCurrency(selectedUser?.hourlyRate || preview.hourlyRate)}/hr</span>
              </div>
              <div className="border-t border-blue-200 pt-2 flex justify-between font-bold text-blue-900">
                <span>Estimated Total</span>
                <span>{fmtCurrency(preview.total)}</span>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium"
            >
              {isSubmitting ? 'Generating...' : 'Generate Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- main page ----
export default function Invoices() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({ status: '', userId: '', projectId: '' });

  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await api.get('/api/users');
      return res.data?.data || [];
    },
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-list'],
    queryFn: async () => {
      const res = await api.get('/api/projects');
      return res.data?.data || [];
    },
  });

  const filterParams = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.status) p.set('status', filters.status);
    if (filters.userId) p.set('userId', filters.userId);
    if (filters.projectId) p.set('projectId', filters.projectId);
    return p.toString();
  }, [filters]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['invoices', filterParams],
    queryFn: async () => {
      const res = await api.get(`/api/invoices?${filterParams}`);
      return res.data?.data || [];
    },
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/api/invoices/${id}`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoices'] }),
  });

  const handleDownloadPDF = (invoice) => {
    const url = `${import.meta.env.VITE_API_URL || 'https://api.haydevs.com'}/api/invoices/${invoice.id}/pdf`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoice.id}.pdf`;
    a.click();
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <p className="text-red-600 font-medium">Failed to load invoices.</p>
        <button onClick={() => refetch()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          Retry
        </button>
      </div>
    );
  }

  const invoices = data || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Filters */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="SENT">Sent</option>
            <option value="PAID">Paid</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
          <select
            value={filters.userId}
            onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Employees</option>
            {(usersData || []).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Project</label>
          <select
            value={filters.projectId}
            onChange={(e) => setFilters((f) => ({ ...f, projectId: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Projects</option>
            {(projectsData || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowModal(true)}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Generate Invoice
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Date Range</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Hours</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      {[1, 2, 3, 4, 5, 6, 7].map((j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 bg-gray-200 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                : invoices.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <FileText className="w-8 h-8 text-gray-300" />
                          <p className="text-sm text-gray-400">No invoices found.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    invoices.map((inv) => (
                      <tr key={inv.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{inv.userName}</td>
                        <td className="px-4 py-3 text-gray-600">{inv.projectName}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {fmtDate(inv.dateFrom)} – {fmtDate(inv.dateTo)}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{fmtHours(inv.hours)}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{fmtCurrency(inv.amount)}</td>
                        <td className="px-4 py-3">
                          {isAdmin ? (
                            <select
                              value={inv.status}
                              onChange={(e) => updateStatus.mutate({ id: inv.id, status: e.target.value })}
                              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="DRAFT">Draft</option>
                              <option value="SENT">Sent</option>
                              <option value="PAID">Paid</option>
                            </select>
                          ) : (
                            <StatusBadge status={inv.status} />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleDownloadPDF(inv)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors"
                          >
                            <Download className="w-3 h-3" />
                            PDF
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <GenerateInvoiceModal
          onClose={() => setShowModal(false)}
          onSuccess={handleModalSuccess}
          users={usersData || []}
          projects={projectsData || []}
        />
      )}
    </div>
  );
}
